import { cn } from '@/shared/utils/utils';

/**
 * ZYNX brand assets (by DurraniTech).
 *
 * The wordmark is reproduced as a scalable CSS gradient mark — "ZYN" in brushed
 * silver and "X" in a warm metallic tone — so it stays crisp at any size and
 * adapts to light/dark surfaces. Brand gradients intentionally sit outside the
 * semantic token palette (they are a fixed brand asset).
 *
 * `tone="light"` → for dark backgrounds (silver/warm metallic).
 * `tone="dark"`  → for light backgrounds (ink/amber).
 */

type Tone = 'light' | 'dark';

export function ZynxMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'relative inline-flex items-center justify-center overflow-hidden rounded-card bg-gradient-to-br from-slate-700 via-slate-800 to-slate-950 shadow-surface-2 ring-1 ring-white/10',
        className,
      )}
      aria-hidden
    >
      {/* subtle metallic sheen */}
      <span className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
      <span className="bg-gradient-to-b from-white to-slate-400 bg-clip-text font-bold leading-none text-transparent" style={{ fontSize: '58%' }}>
        Z
      </span>
    </span>
  );
}

export function ZynxWordmark({ tone = 'dark', className }: { tone?: Tone; className?: string }) {
  const zyn =
    tone === 'light'
      ? 'bg-gradient-to-b from-white via-slate-200 to-slate-400'
      : 'bg-gradient-to-b from-slate-700 via-slate-800 to-slate-950';
  const x =
    tone === 'light'
      ? 'bg-gradient-to-b from-amber-100 via-amber-300 to-amber-700'
      : 'bg-gradient-to-b from-amber-500 to-amber-800';
  return (
    <span className={cn('select-none font-bold leading-none tracking-[0.16em]', className)}>
      <span className={cn('bg-clip-text text-transparent', zyn)}>ZYN</span>
      <span className={cn('bg-clip-text text-transparent', x)}>X</span>
    </span>
  );
}

interface ZynxLogoProps {
  tone?: Tone;
  /** Size classes for the square mark (e.g. "h-8 w-8"). */
  markClassName?: string;
  /** Size class for the wordmark text (e.g. "text-data-lg"). */
  wordClassName?: string;
  /** Show "BY DURRANITECH" tagline under the wordmark. */
  tagline?: boolean;
  className?: string;
}

export function ZynxLogo({
  tone = 'dark',
  markClassName = 'h-9 w-9',
  wordClassName = 'text-headline-md',
  tagline = false,
  className,
}: ZynxLogoProps) {
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <ZynxMark className={markClassName} />
      <span className="flex flex-col">
        <ZynxWordmark tone={tone} className={wordClassName} />
        {tagline && (
          <span
            className={cn(
              'mt-1 text-[10px] font-medium uppercase tracking-[0.34em]',
              tone === 'light' ? 'text-white/55' : 'text-on-surface-variant',
            )}
          >
            by DurraniTech
          </span>
        )}
      </span>
    </span>
  );
}
